import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Vector3Tuple } from 'three';

import { CONSTANTS } from '@/config/constants';

import { getCameraSettingsForBBox, Viewport } from '../../utils/camera';
import { translateVector3 } from '../../utils/vectors';
import { load3DModel } from '../bboxes/bboxes.thunks';
import { exitEditor } from '../project/project.actions';

import { setCameraToBbox } from './camera.thunks';

export type CameraSliceState = {
  viewport: Viewport;
  position: Vector3Tuple;
  rotation: Vector3Tuple;
  zoom: number;
  target: Vector3Tuple;
};

const initialState: CameraSliceState = {
  viewport: { width: 0, height: 0 },
  position: [...CONSTANTS.camera.position],
  rotation: [0, 0, 0],
  zoom: CONSTANTS.camera.defaultZoom as number,
  target: CONSTANTS.camera.defaultTarget as Vector3Tuple,
};

export const cameraSlice = createSlice({
  name: 'camera',
  initialState,
  reducers: {
    updateViewport: (
      state,
      action: PayloadAction<{ width: number; height: number }>,
    ) => {
      state.viewport = action.payload;
    },
    updateCamera: (
      state,
      action: PayloadAction<{
        position?: Vector3Tuple;
        rotation?: Vector3Tuple;
        zoom?: number;
        target?: Vector3Tuple;
      }>,
    ) => {
      const { position, rotation, zoom, target } = action.payload;
      if (typeof position !== 'undefined') state.position = position;
      if (typeof rotation !== 'undefined') state.rotation = rotation;
      if (typeof zoom !== 'undefined') state.zoom = zoom;
      if (typeof target !== 'undefined') {
        state.target = target;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(load3DModel.fulfilled, (state, action) => {
      if (!action.payload || action.payload.baseName !== '$root') {
        return;
      }

      const { bbox, view } = action.payload;

      const offsetCenter = translateVector3(bbox.center, view!.offset);
      const { zoom } = getCameraSettingsForBBox(state.viewport, {
        center: offsetCenter,
        radius: bbox.radius,
      });

      state.zoom = zoom;
      state.target = offsetCenter.toArray();
    });
    builder.addCase(setCameraToBbox.fulfilled, (state, action) => {
      if (!action.payload) {
        return {
          ...initialState,
          viewport: state.viewport,
        };
      }
      return {
        ...state,
        ...action.payload,
        position: initialState.position,
      };
    });
    builder.addCase(exitEditor, () => {
      return initialState;
    });
  },
});

export const { updateCamera, updateViewport } = cameraSlice.actions;
export const cameraReducer = cameraSlice.reducer;
